import { InternalServerError, NotFoundError } from './http';

class InvalidFirebaseIdTokenError extends Error {
  constructor({ message, error }: { message?: string; error?: Error | unknown } = {}) {
    super(message ?? 'Failed to validate Firebase id token.');
    if (error instanceof Error) {
      this.cause = error?.cause;
    }
    this.status = 400;
    this.name = 'InvalidFirebaseIdTokenError';
  }

  readonly status: number;
}

class FirestoreNotFoundError extends NotFoundError {
  constructor({ path }: { path: string }) {
    super(`Data with path/query "${path}" not found in Firestore`);
    this.status = 404;
    this.name = 'FirestoreNotFoundError';
  }

  readonly status: number;
}

class FirestoreResourceLockedError extends NotFoundError {
  constructor({ path }: { path: string }) {
    super(`Resource at path "${path}" is locked`);
    this.status = 423;
    this.name = 'FirestoreResourceLockedError';
  }

  readonly status: number;
}

class FirebaseStorageNotFoundError extends NotFoundError {
  constructor({
    path,
    message = 'Object not found in firebase storage',
  }: {
    path?: string;
    message?: string;
  }) {
    super(path ? `Data with path/query "${path}" not found in storage` : message);

    this.name = 'FirebaseStorageNotFoundError';
  }
}

class FirebaseStorageInvalidTypeError extends InternalServerError {
  constructor({ path, type }: { path: string; type: string | undefined }) {
    super(`File at path ${path} has invalid extension/mime type: ${type ?? 'Unknown'}`);
    this.name = 'FirebaseStorageInvalidTypeError';
  }
}

class FirestoreFieldNotFoundError extends NotFoundError {
  constructor({ path, fieldValue }: { path: string; fieldValue: string }) {
    super(`Field ${fieldValue} missing in data with path ${path}`);
    this.name = 'FirestoreFieldNotFoundError';
  }
}

class FirebaseUpdateAuthEmailError extends Error {
  constructor({ message }: { message: string }) {
    super(message);
    this.name = 'FirebaseUpdateAuthEmailError';
  }
}

export const isFirestoreNotFoundError = (error: unknown): error is FirestoreNotFoundError =>
  error instanceof FirestoreNotFoundError &&
  error.status === 404 &&
  error.message.startsWith('Data with path/query');

export {
  FirebaseStorageInvalidTypeError,
  FirebaseStorageNotFoundError,
  FirebaseUpdateAuthEmailError,
  FirestoreFieldNotFoundError,
  FirestoreNotFoundError,
  FirestoreResourceLockedError,
  InvalidFirebaseIdTokenError,
};
